import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Dialog } from "@mui/material";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Switch,
  Typography,
} from "@mui/material";

import { FrmSelectFromObjects } from "components/formFields/FrmSelectFromObjects";
import { FrmTextField } from "components/formFields/FrmTextField";
import { ButtonProgress } from "components/utility/ButtonProgress";
import { FormErrors } from "components/utility/FormErrors";
import { VSpace } from "components/utility/VSpace";

import {
  getSystemUserCompanyData,
  postSupportAccessActivation,
  getSytemCompanyUsers,
} from "services/sosInventoryService/sosApi";
import { isValid } from "services/sosInventoryService/supportAccessActivation/isValid";
import { setBearerToken } from "services/utility/authentication";
import { getCompanyFromId } from "services/utility/authentication";

import { useErrors } from "hooks/useErrors";

import { openAlert } from "globalState/alertSlice";
import {
  setCompany,
  setCompanyList,
} from "globalState/userCompanySettingsSlice";

export function ActivateDialog(props) {
  const { open, onClose } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [users, setUsers] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { errors, setErrors, isInError } = useErrors();
  const [record, setRecord] = useState({
    companyId: null,
    user: null,
    showUser: false,
    privilegeProfile: "admin",
  });

  function handleInputFieldChange(fieldName, value) {
    setRecord((prevRecord) => ({ ...prevRecord, [fieldName]: value }));
  }

  async function handleActivation() {
    const errors = isValid(record);
    if (errors.messages.length > 0) {
      setErrors(errors);
      return;
    }
    setIsLoading(true);
    const body = {
      ...record,
      user: record.user?.id || null,
      privilegeProfile: record.showUser ? record.user?.id : "admin",
    };
    delete body.showUser;
    delete body.user;
    const { success, message, data } = await postSupportAccessActivation(body);
    if (success) {
      const { token, companyId } = data;

      // get new company data
      const response = await getSystemUserCompanyData();
      const { company, companies } = getCompanyFromId(response, companyId);
      dispatch(setCompanyList(companies));
      dispatch(setCompany(company));
      setBearerToken(token.access_token);
      setIsLoading(false);
      onClose();
      const message = "Successfully created support access activation";
      dispatch(openAlert({ type: "success", message }));
      navigate("/");
    } else {
      dispatch(openAlert({ type: "error", message }));
      setIsLoading(false);
    }
  }

  useEffect(() => {
    async function getCompanyUsers() {
      setUsers(null);
      const users = await getSytemCompanyUsers(record.companyId);
      const mappedUsers = users.map(({ email }) => ({
        id: email,
        name: email,
      }));
      setUsers(mappedUsers);
    }
    if (record.companyId && record.showUser) {
      getCompanyUsers();
    }
  }, [record.companyId, record.showUser]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ inset: "0 0 10%" }}
    >
      <DialogTitle>Enter info</DialogTitle>
      <DialogContent>
        <FormErrors errors={errors} setErrors={setErrors} />
        <VSpace space={1} />
        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
          <div style={{ width: "12rem" }}>
            <FrmTextField
              number
              name="companyId"
              label="Company ID"
              onValueBlur={handleInputFieldChange}
              value={record.companyId}
              error={isInError("companyId")}
            />
          </div>
        </Box>
        <Box sx={{ display: "flex", top: "-1px", gap: 1, mt: 1 }}>
          <Typography component="span">Admin</Typography>
          <Switch
            size="small"
            checked={record.showUser}
            onChange={(e) =>
              handleInputFieldChange("showUser", e.target.checked)
            }
          />
          <Typography component="span">Specific user privileges</Typography>
        </Box>
        {record.showUser && (
          <FrmSelectFromObjects
            name="user"
            label="User"
            options={record.companyId ? users : []}
            value={record.user}
            onValueChange={handleInputFieldChange}
          />
        )}

        <VSpace space={1} />
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <div style={{ position: "relative" }}>
          <Button
            disabled={isLoading}
            color="secondary"
            onClick={handleActivation}
          >
            Activate support access
          </Button>
          {isLoading && <ButtonProgress color="secondary" />}
        </div>
      </DialogActions>
    </Dialog>
  );
}
